import type { ProductData, CompetitorProduct } from '../../../pages/competitor/match-sku/models/types';
import { parseSize, parsePack } from '../../../pages/competitor/match-sku/models/types';
import type { CompetitorClient, InsertCompetitorProductRequest } from '../../../infrastructure/CompetitorClient';

const computeNormalization = (
  competitorSize: string,
  competitorPack: string,
  ourSize: string,
  ourPack: string,
): number => {
  const compTotal = parseSize(competitorSize) * parsePack(competitorPack);
  const ssTotal = parseSize(ourSize) * parsePack(ourPack);
  if (compTotal === 0) return 1;
  return ssTotal / compTotal;
};

export const useInsertCompetitorProduct = (
  competitorClient: CompetitorClient,
  selectedProduct: ProductData,
  onProductDataUpdate: (updatedProduct: ProductData) => void,
  setActiveCompetitor: (competitor: string | null) => void,
) => {
  const handleInsert = async (data: InsertCompetitorProductRequest): Promise<void> => {
    try {
      await competitorClient.insertCompetitorProduct(data);

      const { size: ourSize, pack: ourPack } = selectedProduct.skinSeoulProduct;

      const convertedProducts: CompetitorProduct[] = data.products.map((product) => {
        const size = product.size ?? '';
        const pack = product.pack ?? '';
        const ratio = computeNormalization(size, pack, ourSize, ourPack);
        const usdPrice = product.price?.find((p) => p.currency === 'USD') ?? product.price?.[0];
        const salesPrice = usdPrice?.salesPrice ?? usdPrice?.originalPrice;
        const normalizedPrice = salesPrice === undefined ? undefined : salesPrice * ratio;

        return {
          ...product,
          possibility: 1,
          modifiedAt: new Date().toISOString(),
          category: Array.isArray(product.category) ? product.category.join(', ') : product.category,
          size,
          pack,
          normalizedName: product.name,
          productImageUrls: Array.isArray(product.productImageUrls)
            ? product.productImageUrls.join(', ')
            : product.productImageUrls || '',
          normalizationRatio: ratio,
          normalizedPrice,
          normalization: {
            competitorSize: size,
            competitorPack: pack,
            ourSize,
            ourPack,
            computedRatio: ratio,
            normalizedPrice,
          },
        };
      });

      const updatedProductData: ProductData = {
        ...selectedProduct,
        machineMatchedProducts: [...selectedProduct.machineMatchedProducts],
      };

      const competitorIndex = updatedProductData.machineMatchedProducts.findIndex(
        (comp) => comp.competitor === data.competitor,
      );

      if (competitorIndex >= 0) {
        const competitor = updatedProductData.machineMatchedProducts[competitorIndex];
        if (competitor) {
          updatedProductData.machineMatchedProducts[competitorIndex] = {
            ...competitor,
            competitorProducts: [...(competitor.competitorProducts || []), ...convertedProducts],
          };
        }
      } else {
        updatedProductData.machineMatchedProducts.push({
          competitor: data.competitor,
          competitorProducts: convertedProducts,
        });
      }

      onProductDataUpdate(updatedProductData);
      setActiveCompetitor(data.competitor);
    } catch (error) {
      console.error('Error inserting competitor product:', error);
    }
  };

  return { handleInsert };
};
